/**
 * @fileoverview Wallet Service for Zcash integration
 * Handles wallet connections, address management, and transactions
 */

export interface WalletAddress {
	address: string
	type: 'transparent' | 'shielded'
	balance: number
}

export interface WalletConnection {
	connected: boolean
	addresses: WalletAddress[]
	selectedAddress?: string
	totalBalance: number
}

export interface TransactionRequest {
	from: string
	to: string
	amount: number
	memo?: string
	privateKey?: string
}

export interface TransactionResult {
	txid: string
	success: boolean
	error?: string
}

class WalletService {
	private apiUrl: string
	private connection: WalletConnection

	constructor() {
		this.apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3500'
		this.connection = {
			connected: false,
			addresses: [],
			totalBalance: 0
		}
	}

	/**
	 * Check if wallet is connected
	 */
	isConnected(): boolean {
		return this.connection.connected
	}

	/**
	 * Get current wallet connection state
	 */
	getConnection(): WalletConnection {
		return this.connection
	}

	/**
	 * Connect to Zcash wallet (simulation mode for development)
	 * In production, this would connect to a local Zcash wallet or browser extension
	 */
	async connect(): Promise<WalletConnection> {
		try {
			// For development, we'll simulate wallet connection
			// In production, this would use actual Zcash RPC or wallet extension
			
			// Simulate getting addresses from wallet
			const mockAddresses: WalletAddress[] = [
				{
					address: 'zs1simulation_shielded_address_1234567890abcdef',
					type: 'shielded',
					balance: 2.5
				},
				{
					address: 't1SimulationTransparentAddress123456789',
					type: 'transparent', 
					balance: 1.2
				}
			]

			this.connection = {
				connected: true,
				addresses: mockAddresses,
				selectedAddress: mockAddresses[0].address,
				totalBalance: mockAddresses.reduce((sum, addr) => sum + addr.balance, 0)
			}

			// Store connection in localStorage for persistence
			localStorage.setItem('walletConnection', JSON.stringify(this.connection))

			return this.connection

		} catch (error) {
			console.error('Failed to connect wallet:', error)
			throw new Error('Failed to connect to Zcash wallet')
		}
	}

	/**
	 * Disconnect wallet
	 */
	async disconnect(): Promise<void> {
		this.connection = {
			connected: false,
			addresses: [],
			totalBalance: 0
		}
		
		localStorage.removeItem('walletConnection')
	}

	/**
	 * Restore connection from localStorage
	 */
	async restoreConnection(): Promise<WalletConnection> {
		try {
			const stored = localStorage.getItem('walletConnection')
			if (stored) {
				const connection = JSON.parse(stored) as WalletConnection
				
				// Re-validate connection and refresh balances
				if (connection.connected) {
					this.connection = connection
					await this.refreshBalances()
				}
			}
			
			return this.connection
		} catch (error) {
			console.error('Failed to restore wallet connection:', error)
			return this.connection
		}
	}

	/**
	 * Select a specific address as active
	 */
	selectAddress(address: string): void {
		const addr = this.connection.addresses.find(a => a.address === address)
		if (addr) {
			this.connection.selectedAddress = address
			
			// Update localStorage
			localStorage.setItem('walletConnection', JSON.stringify(this.connection))
		}
	}

	/**
	 * Get selected address
	 */
	getSelectedAddress(): WalletAddress | undefined {
		if (!this.connection.selectedAddress) return undefined
		
		return this.connection.addresses.find(
			addr => addr.address === this.connection.selectedAddress
		)
	}

	/**
	 * Refresh wallet balances
	 */
	async refreshBalances(): Promise<void> {
		if (!this.connection.connected) return

		try {
			// In development, simulate balance refresh
			// In production, query actual wallet balances
			for (const address of this.connection.addresses) {
				// Simulate small balance changes
				address.balance += (Math.random() - 0.5) * 0.01
				if (address.balance < 0) address.balance = 0
			}

			this.connection.totalBalance = this.connection.addresses.reduce(
				(sum, addr) => sum + addr.balance, 0
			)

			// Update localStorage
			localStorage.setItem('walletConnection', JSON.stringify(this.connection))

		} catch (error) {
			console.error('Failed to refresh balances:', error)
		}
	}

	/**
	 * Send a transaction
	 */
	async sendTransaction(request: TransactionRequest): Promise<TransactionResult> {
		try {
			if (!this.connection.connected) {
				throw new Error('Wallet not connected')
			}

			// For development, simulate transaction
			// In production, this would create and broadcast actual transactions
			const response = await fetch(`${this.apiUrl}/api/nfpt/transfer`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					from_address: request.from,
					to_address: request.to,
					amount: request.amount,
					memo: request.memo,
					private_key: request.privateKey
				})
			})

			if (!response.ok) {
				throw new Error(`Transaction failed: ${response.statusText}`)
			}

			const result = await response.json()
			
			if (result.status === 'success') {
				// Update local balance after successful transaction
				await this.refreshBalances()
				
				return {
					txid: result.data.txid,
					success: true
				}
			} else {
				return {
					txid: '',
					success: false,
					error: result.message
				}
			}

		} catch (error) {
			console.error('Transaction failed:', error)
			return {
				txid: '',
				success: false,
				error: error instanceof Error ? error.message : 'Unknown error'
			}
		}
	}

	/**
	 * Create a shielded transaction for NFPT operations
	 */
	async createNFPTTransaction(
		memo: string,
		amount: number = 0.001,
		privateKey?: string
	): Promise<TransactionResult> {
		const selectedAddr = this.getSelectedAddress()
		if (!selectedAddr) {
			return {
				txid: '',
				success: false,
				error: 'No address selected'
			}
		}

		// For NFPT transactions, we need to send to a registry address
		// This would be configurable based on the NFPT operation
		return this.sendTransaction({
			from: selectedAddr.address,
			to: selectedAddr.address, // Self-send with memo
			amount,
			memo,
			privateKey
		})
	}

	/**
	 * Estimate transaction fee
	 */
	async estimateFee(amount: number, isShielded: boolean = true): Promise<number> {
		// Zcash fee estimation
		// Shielded transactions typically cost more than transparent
		return isShielded ? 0.0001 : 0.00001
	}

	/**
	 * Get transaction history for an address
	 */
	async getTransactionHistory(address: string): Promise<any[]> {
		try {
			const response = await fetch(
				`${this.apiUrl}/api/wallet/transactions?address=${encodeURIComponent(address)}`
			)
			
			if (response.ok) {
				const result = await response.json()
				return result.data || []
			}
			
			return []
		} catch (error) {
			console.error('Failed to get transaction history:', error)
			return []
		}
	}
}

// Export singleton instance
export const walletService = new WalletService()
export default walletService 